import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { MarketingNav, MarketingFooter } from '../../components/MarketingChrome';
import { Reveal } from '../../components/Reveal';
import { RuleChip } from '../../components/RuleChip';

const FAQS: Array<{ category: string; items: Array<{ q: string; a: ReactNode }> }> = [
  {
    category: 'Safety & AI',
    items: [
      {
        q: 'Does the AI ever approve or deny a permit?',
        a: 'No. The conflict engine that produces CLEAR, ADVISORY, or CRITICAL_STOP_WORK results is entirely deterministic — spatial geometry (PostGIS), a topological isolation graph, and explicit temporal rules. The LLM is only used to parse free-text scope into candidate equipment tags and to write a plain-language explanation of a finding that already exists. Neither of those AI calls can create, resolve, or suppress a conflict.',
      },
      {
        q: 'What happens if the AI service is down or errors out?',
        a: 'Tag extraction and explanation both degrade gracefully to a clearly-labeled UNAVAILABLE / DETERMINISTIC_TEMPLATE state — the permit workflow continues unaffected because those features were never on the safety-decision path. If the deterministic engine itself cannot complete an evaluation, the result is SYSTEM_INDETERMINATE_BLOCK, which behaves exactly like a stop-work finding. The system never fails open.',
      },
      {
        q: "What is the “AI-generated” badge I see next to some text?",
        a: <>Any text that came from the LLM — a conflict explanation, an extracted tag suggestion — is labeled with a visible disclosure badge distinguishing <RuleChip code="AI_GENERATED" /> content from <RuleChip code="DETERMINISTIC_TEMPLATE" /> content. This is not cosmetic: it is how the product keeps the authoritative and non-authoritative layers honest to the people relying on it.</>,
      },
    ],
  },
  {
    category: 'The conflict engine',
    items: [
      {
        q: 'What kinds of conflicts does it actually catch?',
        a: 'Four families: spatial (hot work / radiography / crane-lift proximity to other active permits), topological (incomplete or unverified isolation — e.g. missing double-block-and-bleed), temporal (expired gas tests, missed fire-watch durations, overlapping planned windows), and SIMOPS-matrix conflicts (incompatible simultaneous operations in the same area, e.g. crane lift over confined-space entry).',
      },
      {
        q: 'Can a critical conflict ever be overridden?',
        a: 'Yes, deliberately — real operations sometimes need to proceed with a compensating control in place. But a CRITICAL_STOP_WORK override requires a written justification, a named mitigation control, and a co-authorization from a second, independent safety authority who is not the person who requested the override. That segregation of duties is enforced by the server, not just the UI.',
      },
      {
        q: 'Why PostGIS instead of a simpler distance check?',
        a: 'Real plants are 3D and irregular — pipe racks, elevated platforms, and underground vaults all matter. PostGIS gives accurate horizontal-distance and elevation-band geometry without standing up a separate spatial service, which keeps the architecture to a single Postgres database.',
      },
    ],
  },
  {
    category: 'Marketplace & billing',
    items: [
      {
        q: 'How does billing work?',
        a: "PermitGuard AI is distributed through the Sensifai Marketplace. Your organization links its Sensifai account once; after that, metered actions (conflict evaluations, AI tag extractions) are reported to the Marketplace as usage events and billed against your organization's subscription and wallet balance, both visible on the Billing & Usage page.",
      },
      {
        q: 'What if my organization is not linked to a Marketplace subscription yet?',
        a: 'The dashboard and billing page detect this and show a direct link to complete linking on the Marketplace rather than an error — the rest of the product (permits, conflicts, isolation) works normally in the meantime, since billing is intentionally decoupled from the safety-critical path.',
      },
      {
        q: 'Is my balance shown always live?',
        a: 'We call the Marketplace Gatekeeper API for a live read on every page load; if that call fails, we fall back to the last successfully cached value and label it clearly as cached with a timestamp, rather than showing a stale number as if it were current.',
      },
    ],
  },
  {
    category: 'Data & deployment',
    items: [
      {
        q: 'Where does plant topology data come from?',
        a: 'Equipment nodes, piping edges, work locations, and elevation levels are modeled directly against your P&ID / plant registry. This deployment ships with a seeded demo refinery; a production rollout needs a real topology import from your P&ID or asset registry (e.g. a DEXPI export, or a SAP PM / Maximo equipment extract) — see the Engineering Report for exactly what we need from you to onboard a real site.',
      },
      {
        q: 'Where are uploaded files and generated reports stored?',
        a: 'On local persistent disk under the deployment, never in third-party object storage — the storage path is configured entirely through an environment variable at deploy time.',
      },
      {
        q: 'Is this multi-tenant?',
        a: 'Yes. Every plant belongs to exactly one organization, every query is scoped to it, and a platform_admin role exists solely for Sensifai operations staff to manage organizations and plans across tenants — it has no special access to any tenant\u2019s safety data beyond what org_admin already has for the same org.',
      },
    ],
  },
];

export default function FaqPage() {
  return (
    <div>
      <MarketingNav />
      <section className="section container-narrow">
        <Reveal>
          <div className="eyebrow">FAQ</div>
          <h1 className="h1-hero" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>Questions worth asking before you trust a safety system.</h1>
        </Reveal>
      </section>
      <section className="section-tight container-narrow" style={{ paddingTop: 0 }}>
        <div className="stack gap-6">
          {FAQS.map((group) => (
            <Reveal key={group.category}>
              <h2 className="h3" style={{ fontSize: '1.375rem', marginBottom: 12 }}>{group.category}</h2>
              <div className="stack gap-2">
                {group.items.map((item) => (
                  <FaqItem key={item.q} q={item.q} a={item.a} />
                ))}
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <section className="section container" style={{ textAlign: 'center', background: 'var(--surface-alt)' }}>
        <Reveal>
          <h2 className="h2-section">Still have a question?</h2>
          <p className="body-text" style={{ marginTop: 8 }}>The live demo is the fastest way to get a concrete answer.</p>
          <div className="hero-cta-row" style={{ justifyContent: 'center' }}>
            <Link to="/login" className="btn btn-primary">Try the demo <ArrowRight size={16} /></Link>
          </div>
        </Reveal>
      </section>
      <MarketingFooter />
    </div>
  );
}

function FaqItem({ q, a }: { q: string; a: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card card-flat" style={{ padding: 0, overflow: 'hidden' }}>
      <button
        className="row"
        style={{ width: '100%', padding: '18px 20px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', font: 'inherit', color: 'var(--text)' }}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span style={{ fontWeight: 600 }}>{q}</span>
        <div className="spacer" />
        <ChevronDown size={18} style={{ transition: 'transform 180ms ease', transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }} />
      </button>
      {open && <div className="body-text" style={{ padding: '0 20px 20px' }}>{a}</div>}
    </div>
  );
}
